import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import Downshift from 'downshift';
import parse from 'html-react-parser';
import type { KeyboardEvent, MouseEvent } from 'react';
import { useCallback, useRef, useState } from 'react';

import { CloseIcon } from '~components/icons/CloseIcon';
import { MagnifyingGlassIcon } from '~components/icons/MagnifyingGlassIcon';
import { queries } from '~modules/media-library/controller/api/Queries';

type Props = {
  value?: string;
  placeholder?: string;
  onChange?: (value: string) => void;
  onClear?: () => void;
  onEnter?: (value?: string) => void;
  onFocus?: () => void;
  onBlur?: () => void;
};
export function InputAutoComplete({ value, placeholder, onChange, onClear, onEnter, onFocus, onBlur }: Props) {
  const [hasFocus, setHasFocus] = useState<boolean>(false);
  const refInput = useRef<HTMLInputElement>(null);

  // Handler: toggle focus and callback
  const toggleFocus = useCallback(
    (focus: boolean) => {
      setHasFocus(focus);
      if (focus) {
        onFocus?.();
      }
      if (!focus) {
        onBlur?.();
      }
    },
    [onBlur, onFocus],
  );

  // Define: find suggestions
  const { data: suggestions } = useQuery({
    ...queries.mediaLibrary.autoComplete(value),
    enabled: !!value && value.length >= 2,
    retry: false,
    refetchOnWindowFocus: false,
    initialData: [],
  });

  // Handle Input value change
  const handleInputValueChange = useCallback(
    inputValue => {
      onChange?.(inputValue);
    },
    [onChange],
  );

  // Handle item selection
  const handleItemSelection = useCallback(
    selectedItem => {
      onEnter?.(selectedItem);
    },
    [onEnter],
  );

  // Handler: internal onClick callback triggers focus of input tag when element is clicked outside of input
  const handleFocusCatchClick = useCallback(() => {
    if (!refInput.current) return;
    if (refInput.current === document.activeElement) return;
    refInput.current.focus();
  }, []);

  // Handler: external onClear callback
  const handleOnClear = useCallback(
    (evt: MouseEvent<HTMLButtonElement>) => {
      evt.stopPropagation();
      onClear?.();
      handleFocusCatchClick();
    },
    [handleFocusCatchClick, onClear],
  );

  const inputValueRegExp = new RegExp(value, 'i'); //  case insensitive RegExp

  return (
    <Downshift
      inputValue={value}
      onInputValueChange={handleInputValueChange} // Handle input value changes
      onChange={handleItemSelection} // Handle item selection
    >
      {({ getInputProps, getItemProps, highlightedIndex, isOpen, getLabelProps, getMenuProps, closeMenu }) => (
        <div className="ml--search-input-container relative">
          <div
            className={clsx(
              'ml--input-search',
              'flex border-solid border p-2 rounded bg-white items-center overflow-hidden cursor-text justify-between',
              hasFocus ? 'border-black' : 'border-silver hover:border-black',
              hasFocus && 'has-focus',
            )}
            onClick={handleFocusCatchClick}
          >
            <label {...getLabelProps({ className: 'ml--input-search--icon pointer-events-none flex items-center' })}>
              <MagnifyingGlassIcon className="w-7 h-7 text-black" />
            </label>
            <input
              {...getInputProps({
                placeholder,
                value,
                ref: refInput,
                onFocus: () => toggleFocus(true),
                onBlur: () => toggleFocus(false),
                onKeyPress: (evt: KeyboardEvent<HTMLInputElement>) => {
                  if (evt.key === 'Enter') {
                    onEnter?.();
                    closeMenu();
                  }
                },
                className:
                  'ml--input-search--input appearance-none border-none outline-none p-0 mx-2 h-8 font-sans text-lg leading-none text-black flex-grow',
              })}
            />
            <button
              className="ml--input-seach--clear appearance-none p-0 -mr-1 border-none outline-none bg-transparent cursor-pointer text-darkgray hover:text-black"
              onClick={handleOnClear}
            >
              <CloseIcon className="w-7 h-7 text-current" />
            </button>
          </div>
          <div className="absolute top-full w-full z-50">
            {isOpen && Boolean(suggestions) && suggestions.length > 0 ? (
              <div
                {...getMenuProps({
                  className:
                    'border-solid border border-silver p-1 rounded bg-white items-center overflow-hidden cursor-default justify-between shadow-lg',
                })}
              >
                {suggestions.slice(0, 10).map((item, index) => (
                  <div
                    key={item}
                    {...getItemProps({
                      index,
                      item,
                      className: clsx('py-1 px-2 rounded font-sans text-sm text-black', {
                        'bg-gray': index === highlightedIndex,
                      }),
                    })}
                  >
                    <div>{parse(item.replace(inputValueRegExp, '<strong>$&</strong>'))}</div>
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        </div>
      )}
    </Downshift>
  );
}
